import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Virtuoso } from "react-virtuoso";
import { Icon, Empty, Skeleton, Input, Button, Popover, message as antdMessage } from "antd";
import moment from "moment";

import { request } from "~/components/Instance";
import { supabase } from "~/lib/supabase";

// Env config
var SUPABASE_URL = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_SUPABASE_URL) || '';
var SUPABASE_ANON_KEY = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_SUPABASE_ANON_KEY) || '';

// Only fetch history for the last N messages; older pages loaded on scroll.
var PAGE_SIZE = 50;

// Local token cache — ~23h TTL, shared key with NotificationContext storage.
var TOKEN_CACHE_KEY = 'contlabs:supabase-token';
function getCachedToken() {
    try {
        var raw = localStorage.getItem(TOKEN_CACHE_KEY);
        if (!raw) return null;
        var parsed = JSON.parse(raw);
        if (!parsed || !parsed.token || !parsed.expires_at) return null;
        if (Date.now() > parsed.expires_at) return null;
        return parsed.token;
    } catch (e) { return null; }
}
function setCachedToken(token) {
    try {
        localStorage.setItem(TOKEN_CACHE_KEY, JSON.stringify({ token: token, expires_at: Date.now() + 23 * 60 * 60 * 1000 }));
    } catch (e) {}
}
function fetchSupabaseToken() {
    var cached = getCachedToken();
    if (cached) return Promise.resolve(cached);
    return request('GET', '/supabase-token', null, false).then(function(res) {
        var token = res && res.data && res.data.token;
        if (token) setCachedToken(token);
        return token;
    });
}

// ─── Styled helpers ─────────────────────────────────────────────────────────

var bannerStyle = function(bg, border, color) {
    return {
        padding: '8px 12px',
        fontSize: 12,
        background: bg,
        borderBottom: '1px solid ' + border,
        color: color,
        display: 'flex',
        alignItems: 'center',
    };
};

// Compact remaining-time formatter for the 24h window: "23h 45m" / "45m" / "30s".
var formatRemaining = function(ms) {
    if (ms <= 0) return '0s';
    var totalMin = Math.floor(ms / 60000);
    if (totalMin < 1) return Math.ceil(ms / 1000) + 's';
    var h = Math.floor(totalMin / 60);
    var m = totalMin % 60;
    if (h > 0) return h + 'h ' + m + 'm';
    return m + 'm';
};

var bubbleStyle = function(isOutbound) {
    return {
        maxWidth: '70%',
        padding: '8px 12px',
        borderRadius: 12,
        background: isOutbound ? '#1890ff' : '#f0f0f0',
        color: isOutbound ? '#fff' : '#262626',
        fontSize: 13,
        lineHeight: 1.4,
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
    };
};

// Memoized bubble — avoids re-rendering every message on every realtime tick.
var MessageBubble = React.memo(function MessageBubble(props) {
    var m = props.message;
    var isOutbound = m.direction === 'outbound';
    var pending = !!m._pending;
    return (
        <div style={{
            display: 'flex',
            justifyContent: isOutbound ? 'flex-end' : 'flex-start',
            padding: '4px 12px',
        }}>
            <div>
                <div style={Object.assign({}, bubbleStyle(isOutbound), { opacity: pending ? 0.6 : 1 })}>
                    {m.text || <em style={{ opacity: 0.6 }}>[anexo]</em>}
                </div>
                <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 2, textAlign: isOutbound ? 'right' : 'left' }}>
                    {pending ? 'enviando…' : moment(m.received_at).format('DD/MM HH:mm')}
                </div>
            </div>
        </div>
    );
});

// ─── Component ──────────────────────────────────────────────────────────────

/**
 * Performant DM thread:
 *   - Fetches last PAGE_SIZE messages via a single filtered REST call
 *   - Virtualized list (Virtuoso) so long threads stay smooth
 *   - One Realtime channel per drawer, filtered server-side by sender_username
 *     so we only wake up for this lead's messages
 *   - Tears down channel + listeners on unmount / handle change
 *
 * @param {object} props
 * @param {string} props.instagramHandle — the lead's @handle (without @)
 */
// Leads often store the Instagram field as a full profile URL
// (e.g. "https://www.instagram.com/fabianopelonha/") instead of a bare
// @handle. Normalize so the rest of the thread always sees just the
// username — used for the realtime filter, the empty-state label, and the
// outbound send call.
var parseInstagramHandle = function(raw) {
    var s = (raw || '').trim();
    if (!s) return '';
    // Strip protocol if present
    s = s.replace(/^https?:\/\//i, '');
    // Strip host so "instagram.com/foo" and "www.instagram.com/foo" both work
    s = s.replace(/^(www\.)?instagram\.com\//i, '');
    // Strip trailing slash, query string, hash
    s = s.split('?')[0].split('#')[0].replace(/\/+$/, '');
    // Strip leading @
    s = s.replace(/^@/, '');
    // Take only the first path segment ("fabianopelonha/reels" → "fabianopelonha")
    s = s.split('/')[0];
    return s;
};

var InstagramThread = function(props) {
    var handle = parseInstagramHandle(props.instagramHandle);

    var [messages, setMessages] = useState([]);
    var [loading, setLoading] = useState(true);
    var [draft, setDraft] = useState('');
    var [sending, setSending] = useState(false);
    var [isConfigured, setIsConfigured] = useState(null); // null = unknown, true/false after check
    // `now` ticks every 30s so the 24h-window countdown updates live without
    // re-fetching messages.
    var [now, setNow] = useState(Date.now());
    var virtuosoRef = useRef(null);

    useEffect(function() {
        var id = setInterval(function() { setNow(Date.now()); }, 30 * 1000);
        return function() { clearInterval(id); };
    }, []);

    // Meta enforces a 24-hour Standard Messaging Window: businesses may only
    // reply within 24h of the lead's last inbound message. Compute window state
    // from the loaded thread so the composer can guard the user up front.
    var windowState = useMemo(function() {
        var WINDOW_MS = 24 * 60 * 60 * 1000;
        var lastInbound = null;
        for (var i = messages.length - 1; i >= 0; i--) {
            if (messages[i].direction === 'inbound') { lastInbound = messages[i]; break; }
        }
        if (!lastInbound) return { state: 'no_inbound' };
        var receivedAtMs = new Date(lastInbound.received_at).getTime();
        var ageMs = now - receivedAtMs;
        if (ageMs >= WINDOW_MS) return { state: 'expired', lastAt: lastInbound.received_at };
        return { state: 'open', remainingMs: WINDOW_MS - ageMs, lastAt: lastInbound.received_at };
    }, [messages, now]);

    var canSend = windowState.state === 'open';

    // Jump-to-bottom helper; passed to Virtuoso after each update.
    var scrollToBottom = useCallback(function() {
        if (virtuosoRef.current) {
            virtuosoRef.current.scrollToIndex({ index: 'LAST', behavior: 'auto' });
        }
    }, []);

    useEffect(function() {
        if (!handle) {
            setMessages([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        setMessages([]);
        setIsConfigured(null);
        var cancelled = false;
        var channel = null;

        var run = async function() {
            var token = await fetchSupabaseToken();
            if (cancelled) return;
            if (!token) {
                setLoading(false);
                return;
            }

            // Realtime requires the JWT to enforce RLS on INSERT events
            supabase.realtime.setAuth(token);

            // 0. Confirm the tenant has an active IG connection. If not, we
            //    short-circuit and render the "not configured" empty state.
            try {
                var connRes = await fetch(SUPABASE_URL + '/rest/v1/instagram_connections?is_active=eq.true&select=page_id&limit=1', {
                    headers: { 'Authorization': 'Bearer ' + token, 'apikey': SUPABASE_ANON_KEY },
                });
                var connRows = connRes.ok ? await connRes.json() : [];
                if (cancelled) return;
                var hasConnection = Array.isArray(connRows) && connRows.length > 0;
                setIsConfigured(hasConnection);
                if (!hasConnection) {
                    setLoading(false);
                    return;
                }
            } catch (e) {
                if (cancelled) return;
                setIsConfigured(false);
                setLoading(false);
                return;
            }

            // 1. Lazy history fetch — server-side filter, ordered newest first for pagination
            //    then reversed so the UI renders oldest→newest.
            var filter = 'sender_username=ilike.' + encodeURIComponent(handle);
            var historyUrl = SUPABASE_URL
                + '/rest/v1/instagram_messages'
                + '?' + filter
                + '&select=id,direction,text,received_at,sender_username,attachments'
                + '&order=received_at.desc'
                + '&limit=' + PAGE_SIZE;

            try {
                var res = await fetch(historyUrl, {
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'apikey': SUPABASE_ANON_KEY,
                    },
                });
                if (cancelled) return;
                if (res.ok) {
                    var rows = await res.json();
                    setMessages(rows.reverse());
                    requestAnimationFrame(scrollToBottom);
                }
            } catch (e) {
                // Silent — empty state will render
            } finally {
                if (!cancelled) setLoading(false);
            }

            // 2. Realtime subscription — Postgres filter so only this lead's messages wake us up
            channel = supabase
                .channel('ig-thread-' + handle.toLowerCase())
                .on(
                    'postgres_changes',
                    {
                        event: 'INSERT',
                        schema: 'public',
                        table: 'instagram_messages',
                        // Case-insensitive match server-side via `ilike` is not supported by Realtime
                        // filters, so we pre-store sender_username exactly and compare lowercased below.
                    },
                    function(payload) {
                        var row = payload.new;
                        if (!row || !row.sender_username) return;
                        if (row.sender_username.toLowerCase() !== handle.toLowerCase()) return;
                        setMessages(function(prev) {
                            // Guard against duplicates if history and realtime race
                            if (prev.some(function(m) { return m.id === row.id; })) return prev;
                            return prev.concat([row]);
                        });
                        requestAnimationFrame(scrollToBottom);
                    },
                )
                .subscribe();
        };

        run();

        return function() {
            cancelled = true;
            if (channel) {
                supabase.removeChannel(channel);
            }
        };
    }, [handle, scrollToBottom]);

    var itemContent = useCallback(function(_index, message) {
        return <MessageBubble message={message} />;
    }, []);

    var handleSend = useCallback(function() {
        var text = draft.trim();
        if (!text || sending || !canSend) return;
        setSending(true);

        // Optimistic insert so the user sees the bubble immediately. Realtime
        // dedup-guards handle the race when the server echo arrives.
        var tempId = 'tmp-' + Date.now();
        var optimistic = {
            id: tempId,
            direction: 'outbound',
            text: text,
            received_at: new Date().toISOString(),
            sender_username: handle,
            _pending: true,
        };
        setMessages(function(prev) { return prev.concat([optimistic]); });
        setDraft('');
        requestAnimationFrame(scrollToBottom);

        fetchSupabaseToken()
            .then(function(token) {
                return fetch(SUPABASE_URL + '/functions/v1/instagram-send', {
                    method: 'POST',
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'apikey': SUPABASE_ANON_KEY,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ sender_username: handle, text: text }),
                });
            })
            .then(function(res) { return res.json().then(function(b) { return { ok: res.ok, body: b }; }); })
            .then(function(result) {
                if (!result.ok) throw new Error((result.body && result.body.error) || 'Falha ao enviar');
                // Replace optimistic row with the server row (keeps message_id consistent with realtime echo)
                var real = result.body && result.body.local;
                setMessages(function(prev) {
                    return prev.map(function(m) {
                        if (m.id !== tempId) return m;
                        return real ? Object.assign({}, optimistic, real, { _pending: false }) : Object.assign({}, optimistic, { _pending: false });
                    });
                });
            })
            .catch(function(err) {
                // Roll the optimistic bubble back and notify the user
                setMessages(function(prev) { return prev.filter(function(m) { return m.id !== tempId; }); });
                antdMessage.error(err.message || 'Erro ao enviar mensagem');
                setDraft(text);
            })
            .finally(function() { setSending(false); });
    }, [draft, sending, handle, scrollToBottom, canSend]);

    if (!handle) {
        return (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40, textAlign: 'center', color: '#8c8c8c' }}>
                <Icon type="instagram" style={{ fontSize: 32, color: '#d9d9d9', marginBottom: 12 }} />
                <div>Informe o @handle do Instagram do lead para ver as mensagens.</div>
            </div>
        );
    }

    if (loading) {
        // Skeleton mirroring the real thread layout so the transition feels instant
        var skeletonRows = [
            { side: 'left',  w: ['55%'] },
            { side: 'right', w: ['40%'] },
            { side: 'left',  w: ['75%', '30%'] },
            { side: 'right', w: ['50%'] },
            { side: 'left',  w: ['35%'] },
            { side: 'right', w: ['60%', '45%'] },
        ];
        return (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#fafafa' }}>
                <div style={{ padding: '8px 12px', background: '#fff', borderBottom: '1px solid #f0f0f0', fontSize: 12, color: '#8c8c8c', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Icon type="instagram" style={{ color: '#E1306C' }} />
                    <span>Conversa com <strong style={{ color: '#262626' }}>@{handle}</strong></span>
                </div>
                <div style={{ flex: 1, overflow: 'hidden', padding: '10px 12px' }}>
                    {skeletonRows.map(function(row, i) {
                        var isRight = row.side === 'right';
                        return (
                            <div key={i} style={{
                                display: 'flex',
                                justifyContent: isRight ? 'flex-end' : 'flex-start',
                                marginBottom: 12,
                            }}>
                                <div style={{
                                    maxWidth: '65%',
                                    padding: '8px 12px',
                                    borderRadius: 12,
                                    background: isRight ? '#e6f7ff' : '#f0f0f0',
                                }}>
                                    <Skeleton
                                        active
                                        title={false}
                                        paragraph={{ rows: row.w.length, width: row.w }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    if (isConfigured === false) {
        return (
            <div style={{ padding: 40, textAlign: 'center' }}>
                <div style={{
                    width: 72, height: 72, borderRadius: 18, margin: '0 auto 16px',
                    background: 'linear-gradient(135deg, #833ab4 0%, #fd1d1d 50%, #fcb045 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 14px rgba(131, 58, 180, 0.25)',
                }}>
                    <Icon type="instagram" style={{ fontSize: 36, color: '#fff' }} />
                </div>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#262626', marginBottom: 6 }}>
                    Instagram não configurado
                </div>
                <div style={{ fontSize: 13, color: '#595959', maxWidth: 340, margin: '0 auto 20px' }}>
                    Conecte sua conta Instagram Business para receber DMs aqui em tempo real.
                </div>
                <Button
                    type="primary"
                    icon="setting"
                    onClick={function() { window.open('/configuracoes/integracoes/instagram', '_blank'); }}
                >
                    Ir para configurações
                </Button>
            </div>
        );
    }

    if (messages.length === 0) {
        return (
            <div style={{ padding: 40 }}>
                <Empty description={<span>Nenhuma mensagem de <strong>@{handle}</strong> ainda.</span>} />
                <div style={{ textAlign: 'center', fontSize: 11, color: '#bfbfbf', marginTop: 12 }}>
                    Novas mensagens aparecerão aqui em tempo real.
                </div>
            </div>
        );
    }

    var windowBanner = (function() {
        var wrap = function(node, popoverProps) {
            return (
                <Popover
                    placement="top"
                    overlayStyle={{ maxWidth: 360 }}
                    title={popoverProps.title}
                    content={popoverProps.content}
                    mouseEnterDelay={0.2}
                >
                    <div style={Object.assign({ cursor: 'help' }, node.props.style)}>
                        {node.props.children}
                    </div>
                </Popover>
            );
        };

        if (windowState.state === 'no_inbound') {
            var node = (
                <div style={bannerStyle('#fffbe6', '#ffe58f', '#ad6800')}>
                    <Icon type="info-circle" style={{ marginRight: 6 }} />
                    Aguardando primeira mensagem do lead. Você só pode responder após o lead enviar uma DM.
                    <Icon type="question-circle" style={{ marginLeft: 'auto', opacity: 0.6 }} />
                </div>
            );
            return wrap(node, {
                title: <span><Icon type="info-circle" style={{ marginRight: 6, color: '#faad14' }} />Aguardando primeira DM</span>,
                content: (
                    <div style={{ fontSize: 12, color: '#595959', lineHeight: 1.6 }}>
                        <p style={{ margin: 0, marginBottom: 8 }}>
                            O Instagram <strong>não permite</strong> que empresas comecem conversas. É o lead que precisa te mandar a primeira mensagem para você poder responder.
                        </p>
                        <div style={{ background: '#fafafa', padding: 8, borderRadius: 6, marginBottom: 6 }}>
                            <strong style={{ fontSize: 11, color: '#262626' }}>Ideias para incentivar a primeira DM:</strong>
                            <ul style={{ margin: '4px 0 0 16px', padding: 0, fontSize: 11 }}>
                                <li>Stories com sticker de pergunta ou enquete</li>
                                <li>"Me chama no direct" em posts e reels</li>
                                <li>Responder comentários convidando para o direct</li>
                                <li>Botão "Enviar mensagem" no perfil</li>
                            </ul>
                        </div>
                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                            Assim que o lead te mandar uma DM, ela aparece aqui na hora.
                        </div>
                    </div>
                ),
            });
        }

        if (windowState.state === 'expired') {
            var nodeExp = (
                <div style={bannerStyle('#fff1f0', '#ffa39e', '#a8071a')}>
                    <Icon type="clock-circle" style={{ marginRight: 6 }} />
                    A janela de 24h expirou (última msg {moment(windowState.lastAt).fromNow()}). Aguarde uma nova DM do lead para responder.
                    <Icon type="question-circle" style={{ marginLeft: 'auto', opacity: 0.6 }} />
                </div>
            );
            return wrap(nodeExp, {
                title: <span><Icon type="clock-circle" style={{ marginRight: 6, color: '#ff4d4f' }} />Janela de 24h expirada</span>,
                content: (
                    <div style={{ fontSize: 12, color: '#595959', lineHeight: 1.6 }}>
                        <p style={{ margin: 0, marginBottom: 8 }}>
                            O Instagram só permite que você responda DMs por até <strong>24 horas</strong> depois da última mensagem do lead. Como já passou desse prazo, novas respostas ficam bloqueadas até ele te falar de novo.
                        </p>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                            <div style={{ flex: 1, background: '#f6ffed', border: '1px solid #b7eb8f', padding: 8, borderRadius: 6 }}>
                                <div style={{ fontSize: 10, fontWeight: 600, color: '#389e0d', marginBottom: 2 }}>REABRIR</div>
                                <div style={{ fontSize: 11, color: '#262626' }}>
                                    Espere o lead te mandar uma nova DM — assim que isso acontecer, você ganha mais 24h para responder.
                                </div>
                            </div>
                            <div style={{ flex: 1, background: '#fffbe6', border: '1px solid #ffe58f', padding: 8, borderRadius: 6 }}>
                                <div style={{ fontSize: 10, fontWeight: 600, color: '#ad6800', marginBottom: 2 }}>ALTERNATIVA</div>
                                <div style={{ fontSize: 11, color: '#262626' }}>
                                    Chame por outro canal (WhatsApp, e-mail, telefone) e peça para ele responder no Instagram.
                                </div>
                            </div>
                        </div>
                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                            Por isso o campo de digitação está desabilitado — qualquer envio agora seria recusado pelo Instagram.
                        </div>
                    </div>
                ),
            });
        }

        // Open window — show subtle countdown
        var hint = formatRemaining(windowState.remainingMs);
        var urgent = windowState.remainingMs < 60 * 60 * 1000; // <1h
        var pctRemaining = Math.max(0, Math.min(100, (windowState.remainingMs / (24 * 60 * 60 * 1000)) * 100));
        var nodeOpen = (
            <div style={bannerStyle(
                urgent ? '#fff7e6' : '#f6ffed',
                urgent ? '#ffd591' : '#b7eb8f',
                urgent ? '#ad4e00' : '#389e0d',
            )}>
                <Icon type={urgent ? 'fire' : 'check-circle'} style={{ marginRight: 6 }} />
                Janela aberta — {hint} para responder.
                <Icon type="question-circle" style={{ marginLeft: 'auto', opacity: 0.6 }} />
            </div>
        );
        return wrap(nodeOpen, {
            title: (
                <span>
                    <Icon type={urgent ? 'fire' : 'check-circle'} style={{ marginRight: 6, color: urgent ? '#fa8c16' : '#52c41a' }} />
                    {urgent ? 'Janela quase fechando' : 'Janela aberta — 24h'}
                </span>
            ),
            content: (
                <div style={{ fontSize: 12, color: '#595959', lineHeight: 1.6 }}>
                    <p style={{ margin: 0, marginBottom: 8 }}>
                        Você tem <strong>{hint}</strong> para responder este lead. Sempre que ele te mandar uma nova DM, esse prazo volta a contar 24h do zero.
                    </p>
                    {/* Visual progress */}
                    <div style={{ marginBottom: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#8c8c8c', marginBottom: 3 }}>
                            <span>Última DM {moment(windowState.lastAt).fromNow()}</span>
                            <span>Expira {moment(windowState.lastAt).add(24, 'hours').fromNow()}</span>
                        </div>
                        <div style={{ height: 6, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{
                                width: pctRemaining + '%',
                                height: '100%',
                                background: urgent
                                    ? 'linear-gradient(90deg, #fa8c16, #ff4d4f)'
                                    : 'linear-gradient(90deg, #52c41a, #389e0d)',
                                transition: 'width 0.5s ease',
                            }} />
                        </div>
                    </div>
                    <div style={{ background: '#fafafa', padding: 8, borderRadius: 6, marginBottom: 6 }}>
                        <strong style={{ fontSize: 11, color: '#262626' }}>Resumo:</strong>
                        <ul style={{ margin: '4px 0 0 16px', padding: 0, fontSize: 11 }}>
                            <li>O Instagram limita a resposta a 24h após a última DM do lead</li>
                            <li>Cada nova DM dele renova o prazo</li>
                            <li>Depois que expira, você só consegue responder se ele te mandar mensagem de novo</li>
                        </ul>
                    </div>
                    {urgent && (
                        <div style={{ fontSize: 11, color: '#fa541c', fontWeight: 500 }}>
                            <Icon type="warning" style={{ marginRight: 4 }} />
                            Resta menos de 1 hora — priorize esta conversa.
                        </div>
                    )}
                </div>
            ),
        });
    })();

    var composeBox = (
        <div style={{ background: '#fff', borderTop: '1px solid #f0f0f0' }}>
            {windowBanner}
            <div style={{ padding: 8, display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <Input.TextArea
                    value={draft}
                    onChange={function(e) { setDraft(e.target.value); }}
                    onPressEnter={function(e) {
                        if (!e.shiftKey) { e.preventDefault(); handleSend(); }
                    }}
                    placeholder={canSend ? 'Digite uma resposta…' : 'Resposta indisponível no momento'}
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    disabled={sending || !canSend}
                    maxLength={1000}
                    style={{ resize: 'none' }}
                />
                <Button
                    type="primary"
                    icon="send"
                    loading={sending}
                    disabled={!draft.trim() || !canSend}
                    onClick={handleSend}
                >
                    Enviar
                </Button>
            </div>
        </div>
    );

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#fafafa' }}>
            <div style={{ padding: '8px 12px', background: '#fff', borderBottom: '1px solid #f0f0f0', fontSize: 12, color: '#8c8c8c', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icon type="instagram" style={{ color: '#E1306C' }} />
                <span>Conversa com <strong style={{ color: '#262626' }}>@{handle}</strong></span>
                <span style={{ marginLeft: 'auto' }}>
                    <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#52c41a', marginRight: 4 }} />
                    Ao vivo
                </span>
            </div>
            <Virtuoso
                ref={virtuosoRef}
                style={{ flex: 1 }}
                data={messages}
                itemContent={itemContent}
                followOutput="auto"
                initialTopMostItemIndex={Math.max(0, messages.length - 1)}
            />
            {composeBox}
        </div>
    );
};

export default React.memo(InstagramThread);
